import React from 'react';
import PropTypes from 'prop-types';
import { useLanguage } from '../../hooks/useLanguage';
import { common as frCommon } from '../../locales/fr/common';
import { common as esCommon } from '../../locales/es/common';
import { common as arCommon } from '../../locales/ar/common';

const translations = {
  fr: frCommon,
  es: esCommon,
  ar: arCommon
};

export default function PaginationControls({
  currentPage,
  totalPages,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  rowsPerPageOptions = [5, 10, 20, 50, 100],
  mobile = false,
  totalItems
}) {
  const { lang } = useLanguage();
  const t = translations[lang];

  return (
    <div className="flex justify-between items-center px-4 py-3 border-t border-gray-200">
      <div className="text-sm text-gray-700">
        {t.pagination.totalItems}: <span className="font-medium">{totalItems}</span> {t.pagination.items}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500">{t.pagination.items}/page</span>
        <select
          className="border rounded px-1 py-0.5 text-xs"
          value={rowsPerPage}
          onChange={e => onRowsPerPageChange(Number(e.target.value))}
        >
          {rowsPerPageOptions.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
        <button
          className="px-2 py-1 rounded border text-xs disabled:opacity-50 bg-white text-gray-900 flex items-center justify-center"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          aria-label={t.pagination.previous}
        >
          {mobile ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          ) : t.pagination.previous}
        </button>
        {totalPages > 10 ? (
          <select
            className="border rounded px-1 py-0.5 text-xs"
            value={currentPage}
            onChange={e => onPageChange(Number(e.target.value))}
          >
            {Array.from({ length: totalPages }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                {t.pagination.page} {i + 1}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-xs">{t.pagination.page} {currentPage} / {totalPages}</span>
        )}
        <button
          className="px-2 py-1 rounded border text-xs disabled:opacity-50 bg-white text-gray-900 flex items-center justify-center"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          aria-label={t.pagination.next}
        >
          {mobile ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          ) : t.pagination.next}
        </button>
      </div>
    </div>
  );
}

PaginationControls.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  mobile: PropTypes.bool,
  totalItems: PropTypes.number.isRequired
}; 